import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ActivityStatusesResponseData } from '../../interfaces/businessDataResponse/activityStatusesResponse';
import { HandleRequestData } from '../../interfaces/handleRequest';
import businessDataApi from '../../api/businessDataApi';
import { BusinessDataApiQueryParams } from '../../interfaces/queryParams';
import { AppDispatch, GlobalState } from '../../../main/store';
import { ExtendedBusinessDataStoreState } from '../../interfaces/businessDataStoreState';
import { ActivityStatusItemData } from '../../interfaces/businessDataItem/activityStatusItem';
import parseBoolean from '../../utils/parseBoolean';
import {
    ExtendedBusinessDataStoreInitialState,
    getActiveBusinessDataValues,
    resetBasicBusinessDataStoreState,
    resetExtendedBusinessDataStoreState,
    initializeBusinessDataListQueryParamsStoreState,
} from '../store.common';

/**
 * activityStatusesSlice manages all activityStatuses state, and contains activityStatuses actions as well as activityStatuses state reducers.
 * Note that while the logic in the reducers appears to mutate the state, it does not.
 * The redux toolkit uses Immer to ensure that no mutations occur.
 */
export const activityStatusesSlice = createSlice({
    name: 'activityStatuses',
    initialState: {
        ...ExtendedBusinessDataStoreInitialState,
    } as ExtendedBusinessDataStoreState<ActivityStatusItemData>,
    reducers: {
        setActivityStatusesList: (
            state,
            action: PayloadAction<ActivityStatusItemData[]>,
        ) => {
            const byActivityStatusName = action.payload.reduce(
                (
                    byActivityStatusName: {
                        [key: string]: ActivityStatusItemData;
                    },
                    activityStatus: ActivityStatusItemData,
                ) => {
                    byActivityStatusName[activityStatus.activity_status] = {
                        ...activityStatus,
                        active: parseBoolean(activityStatus.active),
                    };
                    return byActivityStatusName;
                },
                {},
            );
            state.entities = byActivityStatusName;
            state.keysList = Object.keys(byActivityStatusName);
        },
        setActivityStatus: (
            state,
            action: PayloadAction<ActivityStatusItemData>,
        ) => {
            // if the item is new, add a new key for it and update the count.
            if (!state.entities[action.payload.activity_status]) {
                state.keysList = [
                    ...state.keysList,
                    action.payload.activity_status,
                ];
                state.count = state.keysList.length;
            }
            state.entities[action.payload.activity_status] = action.payload;
        },
        setSelectedActivityStatus: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.selectedItemKey = action.payload;
        },
        setError: (state, action: PayloadAction<any>) => {
            state.error = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsLoaded: (state, action: PayloadAction<boolean>) => {
            state.isLoaded = action.payload;
        },
        setCount: (state, action: PayloadAction<number>) => {
            state.count = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | null>) => {
            state.searchText = action.payload;
        },
        setActive: (
            state,
            action: PayloadAction<BusinessDataApiQueryParams.Active>,
        ) => {
            state.active = action.payload;
        },
        setFrom: (state, action: PayloadAction<number>) => {
            state.from = action.payload;
        },
        setPagesCount: (state, action: PayloadAction<number>) => {
            state.pagesCount = action.payload;
        },
        setCurrentPageIndex: (state, action: PayloadAction<number>) => {
            state.currentPageIndex = action.payload;
        },
        setSize: (state, action: PayloadAction<number>) => {
            state.size = action.payload;
        },
        resetPartialActivityStatusesSlice: resetBasicBusinessDataStoreState,
        resetActivityStatusesSlice: resetExtendedBusinessDataStoreState,
        initializeActivityStatusesListQueryParams:
            initializeBusinessDataListQueryParamsStoreState,
    },
});

export const {
    setIsLoading,
    setIsLoaded,
    setActivityStatusesList,
    setActivityStatus,
    setSelectedActivityStatus,
    setError,
    setCount,
    setSearchText,
    setActive,
    setFrom,
    setPagesCount,
    setCurrentPageIndex,
    setSize,
    resetPartialActivityStatusesSlice,
    resetActivityStatusesSlice,
    initializeActivityStatusesListQueryParams,
} = activityStatusesSlice.actions;

const handleActivityStatusesListRequest = () => {
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        const {
            from,
            size,
            searchText: activity_status,
            active,
        } = state.activityStatuses;

        const params: BusinessDataApiQueryParams.GetActivityStatuses = {
            active,
        };

        // if both "from" and "size" equal to 0, avoid adding them into the query parameters
        if (from !== 0 || size !== 0) {
            params.from = from;
            params.size = size;
        }
        if (activity_status) {
            params.activity_status = activity_status;
        }

        try {
            const {
                result: { ACTIVITY_STATUS, total_count },
            }: HandleRequestData<ActivityStatusesResponseData> =
                await businessDataApi.getActivityStatuses(params);

            dispatch(setActivityStatusesList(ACTIVITY_STATUS));
            dispatch(setCount(total_count));
            dispatch(setPagesCount(Math.ceil(total_count / size)));
        } catch (error: any) {
            dispatch(
                setError(error?.message || 'getActivityStatuses API error'),
            );
        }
    };
};

export const getActivityStatusesList = () => {
    return async (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        dispatch(setIsLoading(true));
        await dispatch(handleActivityStatusesListRequest());
        if (!state.activityStatuses.isLoaded) {
            dispatch(setIsLoaded(true));
        }
        dispatch(setIsLoading(false));
    };
};

/**
 * action to update program
 * before update api call, fetch program types and prepare payload containing only the changed fields
 * after update api call, fetch program types and update the current item in that keysList with the item data we updated
 * @param data
 */
export const updateActivityStatus = (data: ActivityStatusItemData) => {
    return async (
        dispatch: AppDispatch,
        getState: () => GlobalState,
    ): Promise<boolean> => {
        const state = getState();
        // immediately put the keysList into loading while we finish updating the item/fetching the keysList
        dispatch(setIsLoading(true));
        if (!data.pk || data?.pk.length <= 0) {
            // eslint-disable-next-line no-console
            console.error(
                `Error updating new program: required field pk was missing`,
            );
            dispatch(setIsLoading(false));
            return false;
        }
        try {
            await businessDataApi.updateActivityStatus(data.pk, data.active);
            await dispatch(handleActivityStatusesListRequest());
            // force-update the item in the keysList for now, since the keysList we fetched may not have this item's update yet
            dispatch(
                setActivityStatus({
                    ...state.activityStatuses.entities[data.activity_status],
                    active: data.active,
                }),
            );
            dispatch(setIsLoading(false));
            return true;
        } catch (error: any) {
            dispatch(setIsLoading(false));
            return false;
        }
    };
};

export const addActivityStatus = (
    active = true,
    activityStatusName: string,
) => {
    return async (
        dispatch: AppDispatch,
        getState: () => GlobalState,
    ): Promise<boolean> => {
        const state = getState();
        dispatch(setIsLoading(true));
        try {
            await businessDataApi.addActivityStatus(activityStatusName, active);
            // now refresh the keysList
            await dispatch(handleActivityStatusesListRequest());
            if (!state.activityStatuses?.entities[activityStatusName]) {
                // shove the item into the keysList, but only if it's not there already
                const newActivityStatusData: ActivityStatusItemData = {
                    activity_status: activityStatusName,
                    active: active,
                };
                dispatch(setActivityStatus(newActivityStatusData));
            }
            dispatch(setIsLoading(false));
            return true;
        } catch (error: any) {
            dispatch(setIsLoading(false));
            return false;
        }
    };
};

export const selectAllActivityStatuses = (state: GlobalState) => {
    return state.activityStatuses.keysList.map(
        (activityStatusName) =>
            state.activityStatuses.entities[activityStatusName],
    );
};

export const selectAllActiveActivityStatuses = (state: GlobalState) => {
    return getActiveBusinessDataValues(
        state.activityStatuses.keysList,
        state.activityStatuses.entities,
    );
};

export const selectIsLoading = (state: GlobalState) =>
    state.activityStatuses.isLoading;

export const selectIsLoaded = (state: GlobalState) =>
    state.activityStatuses.isLoaded;

export const selectSelectedActivityStatus = (state: GlobalState) => {
    return state?.activityStatuses?.selectedItemKey
        ? state?.activityStatuses?.entities[
              state?.activityStatuses?.selectedItemKey
          ]
        : null;
};

export const selectError = (state: GlobalState) => state.activityStatuses.error;

export const selectCount = (state: GlobalState) => state.activityStatuses.count;

export const selectSearchText = (state: GlobalState) =>
    state.activityStatuses.searchText;

export const selectPagesCount = (state: GlobalState) =>
    state.activityStatuses.pagesCount;

export const selectCurrentPageIndex = (state: GlobalState) =>
    state.activityStatuses.currentPageIndex;

export default activityStatusesSlice.reducer;
